import { useTranslation } from 'react-i18next';
import { Library } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Spinner } from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { AssetLibrary } from '@/components/features/merchants/assets/asset-library';
import { MerchantSelect } from '@/components/features/merchants/merchant-select';
import { useMerchantAssets } from '@/hooks/use-merchant-assets';
import { useMerchants } from '@/hooks/use-merchants';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { useAuth } from '@/hooks/use-auth';
import { isPlatformRole } from '@/types/auth';

const MERCHANT_PICKER_KEY = 'assets.merchantPicker.v1';

function isOptionalString(v: unknown): v is string | undefined {
  return typeof v === 'string' || typeof v === 'undefined';
}

/**
 * Globaler Asset-Bibliothek-Einstieg (Issue #334), erreichbar unter Configuration → Assets.
 *
 * Plattform-Rollen waehlen den Merchant oben per Picker; Merchant-Rollen sehen
 * automatisch nur den eigenen Merchant aus dem Auth-Context (kein Picker). Darunter
 * dieselbe `AssetLibrary` wie die Per-Merchant-Variante (`/merchants/:id/assets`).
 */
export function AssetsPage() {
  const { t } = useTranslation('merchant-assets');
  const { user } = useAuth();

  const isPlatform = user ? isPlatformRole(user.role) : false;

  // Spec 380 §4.2: zuletzt gewaehlten Merchant fuer Plattform-Rollen merken.
  // Merchant-Rollen sind an den eigenen Merchant gepinnt (kein Picker, kein Snapshot).
  const [picked, setPicked] = useLocalStorage<string | undefined>(
    MERCHANT_PICKER_KEY,
    undefined,
    isOptionalString,
  );

  // Merchant-Liste nur fuer Plattform-Rollen laden — gleiche Query-Args wie MerchantSelect
  // intern, daher React-Query-Dedup (kein zweiter Fetch). enabled:isPlatform haelt den
  // Merchant-Rollen-Pfad unveraendert (kein Listen-Fetch).
  const { data: merchants } = useMerchants(
    { status: 'all', limit: 100 },
    { enabled: isPlatform },
  );

  // Domaenen-Validierung (Spec §4.4): den gespeicherten Merchant erst aktivieren, wenn die
  // Liste geladen ist und ihn enthaelt. Waehrend Laden/Fehler oder bei unbekannter/
  // archivierter ID bleibt validatedPicked undefined → Empty-State, kein Asset-Fetch.
  const validatedPicked =
    picked && merchants?.data.some((m) => m.merchantId === picked) ? picked : undefined;

  const merchantId = isPlatform ? validatedPicked : (user?.merchantId ?? undefined);

  const { data: assets, isLoading, error, refetch } = useMerchantAssets(merchantId);

  return (
    <div>
      <PageHeader title={t('page.title')} description={t('global.description')} />

      {isPlatform && (
        <div className="mb-6 max-w-sm">
          <label className="mb-1.5 block text-sm font-medium text-muted-foreground">
            {t('global.selectMerchant')}
          </label>
          <MerchantSelect
            value={validatedPicked}
            onChange={setPicked}
            includeAll={false}
            placeholder={t('global.selectMerchant')}
          />
        </div>
      )}

      {!merchantId ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-20 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Library className="h-6 w-6" />
          </div>
          <p className="max-w-sm text-sm text-muted-foreground">{t('global.prompt')}</p>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <ApiErrorDisplay error={error} onRetry={() => refetch()} />
      ) : (
        <AssetLibrary merchantId={merchantId} assets={assets ?? []} />
      )}
    </div>
  );
}
